import { Component, ReactNode } from 'react';
import { reportError } from '@/lib/errorReporter';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

const isDomManipulationError = (error: Error): boolean => {
  const message = error.message?.toLowerCase() || '';
  return (
    message.includes('removechild') ||
    message.includes('insertbefore') ||
    message.includes('appendchild') ||
    message.includes('not a child of this node') ||
    message.includes('the node to be removed') ||
    message.includes('failed to execute') && message.includes('on \'node\'')
  );
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const isDomError = isDomManipulationError(error);

    // DOM manipulation errors (removeChild/insertBefore "not a child of this node")
    // come from browser extensions (translation tools, password managers, etc.)
    // mutating the DOM out from under React — they are never app bugs. Try to
    // auto-recover, but NEVER report them, even after retries are exhausted.
    // Reporting them here was bypassing the suppression in the global handlers and
    // polluting the production error log with noise.
    if (isDomError) {
      if (this.state.retryCount < 2) {
        console.warn('[ErrorBoundary] DOM manipulation error detected (likely browser extension). Auto-recovering...', error.message);
        this.setState(prev => ({
          hasError: false,
          error: null,
          retryCount: prev.retryCount + 1,
        }));
      } else {
        console.warn('[ErrorBoundary] DOM manipulation error persisted after retries; suppressing report (likely browser extension).', error.message);
      }
      return;
    }

    reportError(error, {
      component: this.props.componentName || 'ErrorBoundary',
      action: 'component_crash',
      errorType: 'react-error',
      componentStack: errorInfo.componentStack || undefined,
      metadata: {
        errorMessage: error.message,
        isDomManipulationError: isDomError,
        retryCount: this.state.retryCount,
      },
    });

    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, retryCount: 0 });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[200px] p-6 bg-destructive/10 rounded-lg border border-destructive/20" data-testid="error-boundary-fallback">
          <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
          <h2 className="text-lg font-semibold text-destructive mb-2">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
            We've logged this error and will look into it. You can try refreshing the page or clicking the button below.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={this.handleReset}
              data-testid="button-try-again"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
            <Button
              variant="default"
              onClick={() => window.location.reload()}
              data-testid="button-refresh-page"
            >
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary componentName={componentName || WrappedComponent.displayName || WrappedComponent.name}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}
